import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import useViewportDimensions, {
  DOCUMENT_PADDING,
  SecondaryHeightLayout,
} from "hooks/viewport-dimensions";
import {
  edgeTypes,
  getOrgLayout,
  nodeTypes,
  DeviceNodeData,
  SourceNodeData,
  OrgChartNodeType,
  DistributionNodeData,
  ZoneNodeData,
  DeviceNodeDevice,
  ZoneLeafNode,
} from "../elements/flow-utils";
import { useTranslation } from "react-i18next";
import GenericCard from "widgets/cards/generic";
import { getReticulation } from "api/reticulation";
import { useSearchParams } from "react-router-dom";
import OrgChartSearchPanel from "../components/search-panel";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";

const nodeWidth = 350;
const nodeHeight = 120;
const clusterWidth = nodeWidth;
const clusterHeight = nodeHeight * 3;

/** Database-ready flow node shape */
export type FlowNodeDb = {
  id: string;
  name: string;
  label: string;
  targets: string[];
  type: OrgChartNodeType;
  leafNodes?: ZoneLeafNode[];
  device?: DeviceNodeDevice;
};

function dbFormatToNodesList(
  dbNodes: FlowNodeDb[]
): Array<
  SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData
> {
  return dbNodes.map((n) => {
    switch (n.type) {
      case OrgChartNodeType.SOURCE:
        return new SourceNodeData(
          n.id,
          n.targets,
          n.name,
          OrgChartNodeType.SOURCE,
          n.device
        );
      case OrgChartNodeType.DISTRIBUTION:
        return new DistributionNodeData(
          n.id,
          n.targets,
          n.name,
          OrgChartNodeType.DISTRIBUTION
        );
      case OrgChartNodeType.ZONE:
        return new ZoneNodeData(
          n.id,
          n.targets,
          n.name,
          OrgChartNodeType.ZONE,
          n.leafNodes ?? []
        );
      case OrgChartNodeType.DEVICE:
        return new DeviceNodeData(
          n.id,
          n.name,
          OrgChartNodeType.DEVICE,
          n.device,
          undefined,
          n.targets ?? []
        );
      default:
        return new DeviceNodeData(
          n.id,
          n.name,
          OrgChartNodeType.DEVICE,
          undefined,
          undefined,
          n.targets ?? []
        );
    }
  });
}

/** Seed nodeIdMapRef so Save preserves existing UUIDs when editing loaded flow */
function seedNodeIdMap(map: Map<string, string>, dbNodes: FlowNodeDb[]): void {
  for (const n of dbNodes) {
    switch (n.type) {
      case OrgChartNodeType.SOURCE:
        if (n.device) map.set(`src:${n.device.snr}`, n.id);
        break;
      case OrgChartNodeType.DISTRIBUTION:
        map.set(`dist:${n.id}`, n.id);
        break;
      case OrgChartNodeType.ZONE:
        map.set(`zone:${n.id}`, n.id);
        break;
      case OrgChartNodeType.DEVICE:
        if (n.device) map.set(`dev:${n.device.snr}`, n.id);
        break;
    }
  }
}

/**
 * Paste your flow log (FlowNodeDb[]) here to load as initial data, as if from backend.
 * Set to `undefined` or `[]` to start empty.
 */
const INITIAL_FLOW_FROM_BACKEND: FlowNodeDb[] | undefined = undefined;

const FlowViewer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const { dimensions } = useViewportDimensions();
  // Extract the filter parameter
  const id = searchParams.get("id");

  const [flowName, setFlowName] = useState("");
  const [flowType, setFlowType] = useState("");

  const [nodesList, setNodesList] = useState<
    Array<SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData>
  >([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [direction, setDirection] = useState<"TB" | "LR">("LR");
  const nodeIdMapRef = useRef<Map<string, string>>(new Map());
  const reactFlowInstanceRef = useRef<{
    fitView: (options?: {
      padding?: number;
      duration?: number;
      nodes?: { id: string }[];
    }) => void;
  } | null>(null);

  const fetchFlow = useCallback(async () => {
    if (!id) return;
    dispatch(setLoading(true));
    const { data, status, isLoading } = await getReticulation(id);
    dispatch(setLoading(isLoading));
    if (status === 200 && data) {
      setFlowName(data.name ?? "");
      setFlowType(data.reticulationType ?? "");
      const nodes = data.config as FlowNodeDb[] | undefined;
      if (nodes?.length) {
        setNodesList(dbFormatToNodesList(nodes));

        seedNodeIdMap(nodeIdMapRef.current, nodes);
      }
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchFlow();
    } else if (INITIAL_FLOW_FROM_BACKEND?.length) {
      setNodesList(dbFormatToNodesList(INITIAL_FLOW_FROM_BACKEND));

      seedNodeIdMap(nodeIdMapRef.current, INITIAL_FLOW_FROM_BACKEND);
    }
  }, [id, fetchFlow]);

  useEffect(() => {
    const { nodes: layoutNodes, edges: layoutEdges } = getOrgLayout(
      nodesList,
      direction,
      { width: nodeWidth, height: nodeHeight },
      { width: 50, height: 50 },
      { width: nodeWidth * 2, height: nodeHeight },
      { width: clusterWidth, height: clusterHeight }
    );
    setEdges(layoutEdges);
    setNodes(layoutNodes);
  }, [nodesList, direction, setEdges, setNodes]);

  const handleSearch = useCallback(
    (search: string) => {
      const term = search.trim().toLowerCase();
      if (!reactFlowInstanceRef.current) return;

      if (!term) {
        reactFlowInstanceRef.current.fitView({ duration: 400 });
        return;
      }

      const getSearchableStrings = (
        data:
          | SourceNodeData
          | DeviceNodeData
          | DistributionNodeData
          | ZoneNodeData
      ): string[] => {
        const strings: string[] = [data.name];
        if ("device" in data && data.device) {
          strings.push(data.device.name, data.device.snr);
        }
        if ("leafNodes" in data && data.leafNodes) {
          data.leafNodes.forEach((leaf) => {
            strings.push(leaf.device.name, leaf.device.snr);
          });
        }
        return strings.filter(Boolean).map((s) => s.toLowerCase());
      };

      type ScoredNode = { id: string; score: number };
      const scored: ScoredNode[] = [];

      nodes.forEach((node) => {
        const data = node.data as
          | SourceNodeData
          | DeviceNodeData
          | DistributionNodeData
          | ZoneNodeData;
        const strings = getSearchableStrings(data);
        let bestScore = 0;
        for (const s of strings) {
          if (s === term) bestScore = Math.max(bestScore, 100);
          else if (s.startsWith(term)) bestScore = Math.max(bestScore, 50);
          else if (s.includes(term)) bestScore = Math.max(bestScore, 25);
        }
        if (bestScore > 0) scored.push({ id: node.id, score: bestScore });
      });

      if (scored.length === 0) return;

      scored.sort((a, b) => b.score - a.score);
      const bestId = scored[0].id;
      reactFlowInstanceRef.current.fitView({
        nodes: [{ id: bestId }],
        padding: 0.2,
        duration: 400,
      });
    },
    [nodes]
  );

  return (
    <SecondaryHeightLayout>
      <div className="w-full" ref={searchPanelRef}>
        <OrgChartSearchPanel
          id="org-chart-search-panel"
          direction={direction}
          onDirectionChange={setDirection}
          title={flowName}
          type={flowType}
          onSearch={handleSearch}
          onRefresh={fetchFlow}
        />
      </div>
      <div
        className="h-full w-full"
        style={{
          height:
            dimensions.contentHeight -
            searchPanelRef.current?.clientHeight -
            DOCUMENT_PADDING,
          marginTop: DOCUMENT_PADDING,
        }}
      >
        <GenericCard extra="flex h-full min-h-0 flex-col !overflow-visible">
          <div className="relative z-0 h-full min-h-0 flex-1">
            <ReactFlow
              className="h-full w-full"
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onInit={(instance) => {
                reactFlowInstanceRef.current = instance;
              }}
              connectionLineType={ConnectionLineType.SmoothStep}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              minZoom={0}
              maxZoom={2}
              fitView
              nodesConnectable={false}
              nodesDraggable={false}
            >
              <Controls />
              <Background />
            </ReactFlow>
          </div>
        </GenericCard>
      </div>
    </SecondaryHeightLayout>
  );
};

export default FlowViewer;
